import { useRef } from 'react';
import { useLocation } from 'react-router';
import { useSnapshot } from 'valtio';

import Text from '@cartwhl/ui/base/Text';
import { SHELL_HEADER_SUBTITLE_CLASSNAME } from '@cartwhl/ui/layout/StudioShellHeader';
import UserMenuWithCredits from '@cartwhl/ui/organisms/UserMenuWithCredits';

import { userStore } from 'stores/global';

interface CanvasLayoutHeaderProps {
  title: string;
  subtitle?: string;
  hasHeaderContent?: boolean;
  hasCenterHeaderContent?: boolean;
  onStartHeaderContentContainerChange?: (node: HTMLDivElement | null) => void;
  onCenterHeaderContentContainerChange?: (node: HTMLDivElement | null) => void;
  onEndHeaderContentContainerChange?: (node: HTMLDivElement | null) => void;
}

export default function CanvasLayoutHeader({
  title,
  subtitle,
  hasHeaderContent = false,
  hasCenterHeaderContent = false,
  onStartHeaderContentContainerChange,
  onCenterHeaderContentContainerChange,
  onEndHeaderContentContainerChange,
}: CanvasLayoutHeaderProps) {
  const { pathname } = useLocation();
  const { loading: userLoading } = useSnapshot(userStore.state);
  const endGroupRef = useRef<HTMLDivElement>(null);

  return (
    <header className="bg-primary relative flex h-16 shrink-0 items-center gap-1 px-4 lg:gap-4 lg:px-6">
      <div
        className={`flex min-w-0 items-center gap-3 lg:gap-[22px] ${hasCenterHeaderContent ? 'shrink-0' : 'flex-1'}`}
      >
        <div
          ref={onStartHeaderContentContainerChange}
          className="flex shrink-0 items-center empty:hidden"
        />
        <Text
          as="h1"
          variant="studio/title/md"
          sm:variant="studio/title/lg"
          className={`text-primary h-5 min-w-0 truncate ${hasCenterHeaderContent ? 'hidden lg:block' : ''}`}
        >
          {title}
          {subtitle && !hasHeaderContent ? (
            <span className={SHELL_HEADER_SUBTITLE_CLASSNAME}>{subtitle}</span>
          ) : null}
        </Text>
      </div>
      <div
        className={`min-w-0 items-center justify-start overflow-hidden lg:justify-center ${hasCenterHeaderContent ? 'flex flex-1' : 'hidden'}`}
      >
        <div
          ref={onCenterHeaderContentContainerChange}
          className="flex min-w-0 max-w-full items-center justify-start overflow-hidden lg:justify-center"
        />
      </div>
      <div ref={endGroupRef} className="flex shrink-0 items-center">
        {!userLoading ? (
          <UserMenuWithCredits
            currentPathname={pathname}
            currentSurface="apps"
            appletHeader
            compactHeaderAtTablet
            middleContent={
              <div
                ref={onEndHeaderContentContainerChange}
                className="flex min-w-0 items-center justify-end overflow-hidden empty:hidden"
              />
            }
          />
        ) : null}
      </div>
    </header>
  );
}
